<template>
  <div class="pagination">
    <nav
      class="pagination-wrap"
      :style="{
        'justify-content': alignment,
      }"
    >
      <ul
        v-if="total > smallestPageSizeOptions"
        :style="{
          order: perPageItemPlacement === 'left' ? 1 : 2,
          display: !pagesSizesVisible && alignment !== 'space-between' ? 'none' : undefined,
          visibility: !pagesSizesVisible && alignment === 'space-between' ? 'hidden' : undefined,
        }"
        class="filtered-pagination"
      >
        <li aria-hidden="true">
          Show
        </li>
        <li
          v-for="item in pageSizeOptions"
          :key="item"
          :class="{ active: pageSize === item }"
        >
          <button
            role="tab"
            :aria-selected="pageSize === item"
            :aria-label="`Display ${item} items`"
            @click="handlePageSizeChange(item)"
          >
            {{ item }}
          </button>
        </li>
      </ul>

      <ul
        v-if="total > pageSize"
        :style="{
          order: perPageItemPlacement === 'left' ? 2 : 1,
        }"
        class="pagination"
      >
        <li
          v-show="currentPageNumber > 1"
          @click.prevent="handlePrevPage"
        >
          <template v-if="prevPageUrl">
            <a
              class="previous-button"
              :href="prevPageUrl"
              :aria-label="previousTitle + ' go back to ' + previousButtonAriaLabel"
            >
              <Icon :icon="csgChevronLargeLeft" />
            </a>
          </template>
          <template v-else>
            <button
              class="previous-button"
              :aria-label="previousTitle + ' go back to ' + previousButtonAriaLabel"
            >
              <Icon :icon="csgChevronLargeLeft" />
            </button>
          </template>
        </li>
        <li>
          <div class="select-container">
            <Dropdown
              :id="id"
              v-model="currentPageRef"
              name="dropdown"
              label="Page"
              :aria-label="selectPageTitle"
              :options="dropdownList"
              dropdown-size="large"
              @change="handleSelectPage"
            />
          </div>
        </li>
        <li
          v-show="currentPageNumber < totalPages"
          @click.prevent="handleNextPage"
        >
          <template v-if="nextPageUrl">
            <a
              class="next-button"
              :href="nextPageUrl"
              :aria-label="nextTitle + ' go forward to ' + nextButtonAriaLabel"
            >
              <Icon :icon="csgChevronLargeRight" />
            </a>
          </template>
          <template v-else>
            <button
              class="next-button"
              :aria-label="nextTitle + ' go forward to ' + nextButtonAriaLabel"
            >
              <Icon :icon="csgChevronLargeRight" />
            </button>
          </template>
        </li>
      </ul>
      <div
        v-else
        class="pagination_total"
      >
        <span>Showing All {{ total }} Items</span>
      </div>
    </nav>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  watch,
} from 'vue';
import { csgChevronLargeRight, csgChevronLargeLeft } from '@csgSvgIcons/icons';
import Icon from '../icon';
import generateRandomId from '../../utils/randomNumber';
import Dropdown from '../dropdown';

export default defineComponent({
  name: 'Pagination',
  components: {
    Dropdown,
    Icon,
  },
  props: {
    id: {
      type: [String, Number],
      default: () => generateRandomId(),
    },
    total: {
      type: Number,
      default: 100,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    defaultPageSize: {
      type: Number,
      default: 60,
    },
    firstPageSize: {
      type: Number,
      default: undefined,
    },
    pageSizeOptions: {
      type: Array,
      default: () => [60, 120],
    },
    nextTitle: {
      type: String,
      default: 'Next page',
    },
    previousTitle: {
      type: String,
      default: 'Previous page',
    },
    nextPageUrl: {
      type: String,
      default: null,
    },
    prevPageUrl: {
      type: String,
      default: null,
    },
    selectPageTitle: {
      type: String,
      default: 'Page',
    },
    perPageItemPlacement: {
      type: String,
      validator: (val) => ['left', 'right'].includes(val),
      default: 'left',
    },
    alignment: {
      type: String,
      validator: (val) => ['flex-start', 'flex-end', 'space-between'].includes(val),
      default: 'flex-start',
    },
    pagesSizesVisible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      csgChevronLargeLeft,
      csgChevronLargeRight,
    };
  },
  emits: ['paginationPageSelect', 'paginationPrevious', 'paginationNext'],
  setup(props, { emit }) {
    const smallestPageSizeOptions = Math.min(...props.pageSizeOptions);
    const pageSize = ref(props.defaultPageSize);
    const currentPageRef = ref(`${props.currentPage}`);
    const currentPageNumber = ref(props.currentPage);

    const totalPages = computed(() => {
      if (props.firstPageSize) {
        const rest = props.total - props.firstPageSize;
        return 1 + Math.ceil(rest / pageSize.value);
      }
      return Math.ceil(props.total / pageSize.value);
    });
    const dropdownList = computed(
      () => Array.from({ length: totalPages.value }, (_, i) => ({
        displayValue: `${i + 1} of ${totalPages.value}`,
        value: i + 1,
      })),
    );
    const nextButtonAriaLabel = computed(() => (
      currentPageNumber.value < dropdownList.value.length ? dropdownList.value[currentPageNumber.value]?.displayValue : null));
    const previousButtonAriaLabel = computed(() => (
      currentPageNumber.value > 1 ? dropdownList.value[currentPageNumber.value - 2]?.displayValue : null));

    const handleSelectPage = (e) => {
      currentPageRef.value = e.target.value;
      emit('paginationPageSelect', pageSize.value, Number(currentPageRef.value));
    };
    const handlePageSizeChange = (newPageSize) => {
      pageSize.value = newPageSize;
      if (currentPageNumber.value > totalPages.value) {
        currentPageRef.value = `${totalPages.value}`;
      }
      emit('paginationPageSelect', pageSize.value, Number(currentPageRef.value));
    };
    const handlePrevPage = () => {
      let pageNumber = currentPageNumber.value;
      if (currentPageNumber.value > 1) {
        currentPageRef.value = `${--pageNumber}`;
      }
      emit('paginationPrevious', pageNumber);
      emit('paginationPageSelect', pageSize.value, pageNumber);
    };
    const handleNextPage = () => {
      emit('paginationNext', currentPageNumber.value);
      let pageNumber = currentPageNumber.value;
      if (currentPageNumber.value < Math.ceil(props.total / pageSize.value)) {
        currentPageRef.value = `${++pageNumber}`;
      }
      emit('paginationPageSelect', pageSize.value, pageNumber);
    };

    watch(
      () => props.currentPage,
      () => {
        if (props.currentPage !== currentPageNumber.value) {
          currentPageRef.value = `${props.currentPage}`;
          emit('paginationPageSelect', pageSize.value, props.currentPage);
        }
      },
    );

    watch(currentPageRef, () => {
      currentPageNumber.value = Number(currentPageRef.value);
    });

    return {
      currentPageRef,
      currentPageNumber,
      pageSize,
      totalPages,
      dropdownList,
      handleSelectPage,
      handlePageSizeChange,
      handlePrevPage,
      handleNextPage,
      smallestPageSizeOptions,
      nextButtonAriaLabel,
      previousButtonAriaLabel,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@import "~@core/csg/src/scss/components/pagination";
@include csg-form-label;
@include csg-form-select;
@include csg-pagination;

.pagination {
  display: flex;
  align-items: center;
}

.pagination-wrap {
  flex-basis: 100%;
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  button {
    cursor: pointer;
  }
  .select-container {
    :deep(.select-container) {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
}

.pagination_total {
  order: 2;
}
</style>
